import * as React from "react";
import { Or } from "../../../../domain/Or";
import Render from "../../../../presentation/Shared/Render";
import { TRenderable } from "../../../../presentation/Shared/SharedTypes";
import { Style } from "../../../../presentation/Shared/Styles/CSS";
import { BannerImage } from "../BannerImage/BannerImage";
import { CustomSwalCSS } from "../CustomSwalCSS/CustomSwalCSS";
import { TSwalOptions } from "../RenderSwal";
import {
    ImageAtCorners,
    ImageAtCornerSets,
} from "../SwalBorders/ImageAtCorners";
import { SwalButtons } from "./SwalButtons";

export function SwalBody({
    options,
    Fulfill,
    Reject,
    children,
}: {
    options: TSwalOptions;
    Fulfill?: Function;
    Reject?: Function;
    children: TRenderable;
}) {
    const showBorders = Or(options.showBorders, true);
    const { bannerImage } = options;
    const height = Or(options.height, `55vh`);
    const buttonDividerHeight = Or(
        options.buttonDividerHeight,
        `56px`
    );
    const contentHeight = options.showButtonDivider
        ? `calc(${height} - ${buttonDividerHeight})`
        : height;
    const topFadeHeight = options.contentTopFadeHeight ?? `44px`;
    const bottomFadeHeight = options.contentBottomFadeHeight ?? `44px`;
    const contentMaskImage =
        options.contentTopFade && options.contentBottomFade
            ? `linear-gradient(to bottom, transparent 0, black ${topFadeHeight}, black calc(100% - ${bottomFadeHeight}), transparent 100%)`
            : options.contentTopFade
              ? `linear-gradient(to bottom, transparent 0, black ${topFadeHeight}, black 100%)`
              : options.contentBottomFade
                ? `linear-gradient(to bottom, black 0, black calc(100% - ${bottomFadeHeight}), transparent 100%)`
                : undefined;

    React.useEffect(() => {
        CustomSwalCSS.SetTitleTextColor();
        CustomSwalCSS.SetBackgroundImageColor();

        if (showBorders) {
            CustomSwalCSS.AddBorder();
        }
    }, []);

    let columnCount = Or(options.columnCount, 1);
    columnCount = GetCalculatedColumnCount(columnCount);

    // column-count property should not be there at all even if it's value is 1
    const columnCSS = columnCount === 1 ? {} : { columnCount };

    return (
        <span id="swalBody">
            <Render id="swalBannerImages" when={showBorders}>
                <ImageAtCorners
                    imageDistance={17}
                    imageAtCornerSet={ImageAtCornerSets.swal}
                />
                {bannerImage ? (
                    <BannerImage
                        bannerImage={bannerImage}
                        columnCount={columnCount}
                    />
                ) : null}
            </Render>
            <div
                id="parentContainer"
                    {...Style(`swalBody_parentContainer_height_${contentHeight}`, {
                        maxHeight: `65vh`,
                        height: contentHeight, // Required to make child container scrollable
                        minHeight: `30px`,
                        width: options.showButtonDivider
                            ? `calc(100% - 10px)`
                            : "100%",
                        margin: options.showButtonDivider ? `0 auto` : undefined,
                        overflow: "hidden",
                        background: options.showButtonDivider
                            ? `radial-gradient(
                                ellipse at 50% 8%,
                                rgba(170, 126, 58, 0.12) 0%,
                                rgba(170, 126, 58, 0.07) 36%,
                                rgba(170, 126, 58, 0.02) 62%,
                                transparent 90%
                            ), repeating-linear-gradient(
                                0deg,
                                rgba(255, 239, 190, 0.012) 0 1px,
                                transparent 1px 4px
                            ), linear-gradient(
                                to right,
                                rgba(0, 0, 0, 0.14),
                                transparent 10%,
                                transparent 90%,
                                rgba(0, 0, 0, 0.14)
                            ), linear-gradient(
                                rgba(35, 29, 18, 0.2),
                                rgba(19, 17, 12, 0.14)
                            )`
                            : undefined,
                        borderRadius: options.showButtonDivider
                            ? `7px 7px 0 0`
                            : undefined,
                        boxSizing: `border-box`,
                })}
            >
                <div
                    id="childContainer"
                    {...Style(`swalBody_childContainer_${columnCount}`, {
                        width: "100%",
                        height: `100%`,
                        overflowY: "scroll",
                        overflowX: `hidden`,
                        paddingRight: "17px", // Hides vertical scrollbar
                        position: `relative`,
                        boxSizing: "content-box",
                        maskImage: contentMaskImage,
                        WebkitMaskImage: contentMaskImage,
                        maskSize: contentMaskImage ? "100% 100%" : undefined,
                        WebkitMaskSize: contentMaskImage
                            ? "100% 100%"
                            : undefined,
                        maskRepeat: contentMaskImage ? "no-repeat" : undefined,
                        WebkitMaskRepeat: contentMaskImage
                            ? "no-repeat"
                            : undefined,
                        ...columnCSS,
                    })}
                >
                    <div
                        id="childContents"
                        {...Style("swalBody_childContents", {
                            width: `100%`,
                        })}
                    >
                        {children}
                    </div>
                </div>
            </div>
            <Buttons
                Fulfill={Fulfill}
                Reject={Reject}
                showDivider={options.showButtonDivider}
                buttonDividerHeight={buttonDividerHeight}
                buttonsAtBottom={options.buttonDividerButtonsAtBottom}
            />
        </span>
    );
}

const Buttons = ({
    Fulfill,
    Reject,
    showDivider,
    buttonDividerHeight,
    buttonsAtBottom,
}: {
    Fulfill?: Function;
    Reject?: Function;
    showDivider?: boolean;
    buttonDividerHeight: string;
    buttonsAtBottom?: boolean;
}) => (
    <div
        id="swalButtons-target"
        {...Style(`swalButtons_${showDivider ? "fadedDivider" : "plain"}`, {
            position: "relative",
            zIndex: showDivider ? 2 : undefined,
            bottom: showDivider ? 0 : "13px",
            right: "50%",
            transform: showDivider ? "translateX(50%)" : "translate(50%, 50%)",
            display: showDivider ? "flex" : "block",
            justifyContent: showDivider ? "center" : undefined,
            height: showDivider ? buttonDividerHeight : undefined,
            boxSizing: "border-box",
            ...(showDivider && buttonsAtBottom
                ? {
                      "& > button": {
                          position: "absolute",
                          bottom: "-1px",
                          left: "50%",
                          zIndex: 1,
                          transform: "translateX(-50%)",
                      },
                      "& > button:hover": {
                          filter: "saturate(0.5) brightness(2)",
                          WebkitTransform: "translateX(-50%)",
                          transform: "translateX(-50%)",
                      },
                  }
                : {}),
            ...(showDivider
                ? {
                      ":before": {
                          content: `""`,
                          position: "absolute",
                          top: "9px",
                          right: 0,
                          left: 0,
                          height: "2px",
                          pointerEvents: "none",
                          backgroundImage: `radial-gradient(
                              circle,
                              rgba(168, 129, 63, 0.34) 0 0.7px,
                              transparent 0.9px
                          )`,
                          backgroundPosition: "center",
                          backgroundRepeat: "repeat-x",
                          backgroundSize: "4px 2px",
                          maskImage: `linear-gradient(
                              to right,
                              transparent 0%,
                              black 18%,
                              black 82%,
                              transparent 100%
                          )`,
                          WebkitMaskImage: `linear-gradient(
                              to right,
                              transparent 0%,
                              black 18%,
                              black 82%,
                              transparent 100%
                          )`,
                      },
                  }
                : {}),
            paddingTop: showDivider ? "19px" : 0,
        })}
    >
        <SwalButtons.OK Fulfill={Fulfill} />
        <SwalButtons.Cancel Cancel={Reject} />
    </div>
);

export function GetCalculatedColumnCount(columnCount: number) {
    return window.innerWidth > 500 && columnCount > 1 ? columnCount : 1;
}
